import { useCallback, useEffect, useMemo, useState } from 'react'
import LanguageToggle from '@/components/ui/LanguageToggle'
import { gql } from '@/lib/api'
import { t } from '@/lib/i18n'
import type { StartScreenMedia } from '@/lib/kiosk-config'
import { KIOSK_ATTRS_QUERY, MENU_QUERY } from '@/lib/queries'
import type { MenuPage, MenuType } from '@/lib/types'
import { isAvailable } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

/** Filtrerar tomma URL:er. */
function resolveSlideshow(startScreenMedia: StartScreenMedia[] | undefined): StartScreenMedia[] {
  if (!startScreenMedia || startScreenMedia.length === 0) return []
  return startScreenMedia.filter((m) => m.url && m.url.trim().length > 0)
}

export default function MenuTypeSelect() {
  const config = useKioskStore((s) => s.config)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const locale = useKioskStore((s) => s.locale)
  const parseToken = useKioskStore((s) => s.parseToken)
  const setStep = useKioskStore((s) => s.setStep)
  const setMenuType = useKioskStore((s) => s.setMenuType)
  const setPages = useKioskStore((s) => s.setPages)
  const setRestaurantName = useKioskStore((s) => s.setRestaurantName)
  const setPaymentMethods = useKioskStore((s) => s.setPaymentMethods)
  const setDeliveryLocations = useKioskStore((s) => s.setDeliveryLocations)
  const setTimeSlots = useKioskStore((s) => s.setTimeSlots)
  const setShowsTip = useKioskStore((s) => s.setShowsTip)
  const setQueueTime = useKioskStore((s) => s.setQueueTime)
  const setSelectedDeliveryLoc = useKioskStore((s) => s.setSelectedDeliveryLoc)
  const setLoading = useKioskStore((s) => s.setLoading)
  const setError = useKioskStore((s) => s.setError)
  const loading = useKioskStore((s) => s.loading)

  // Zustand setters are referentially stable; only re-bind on actual inputs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  const handleSelect = useCallback(
    async (type: MenuType) => {
      if (!config) return
      setMenuType(type)
      setLoading(true)
      setStep('browsing')

      try {
        const data = await gql(config.apiEndpoint, MENU_QUERY, { id: config.restaurantId, menuType: type }, parseToken || undefined)

        const allPages: MenuPage[] = data.restaurant.menu.pages
        setPages(allPages.filter((p) => p.categories.some((c) => c.products.some(isAvailable))))
        setRestaurantName(data.restaurant.name || '')

        // (menu.addonProducts togs bort från MENU_QUERY — anon-kiosken har inte
        // access till den. Bilder på items läses istället direkt från
        // alternativeItem.addonProduct.properties.imageUrl i native-rendering.)
        setPaymentMethods(
          (data.restaurant.paymentMethods || []).filter(
            (m: { paymentProvider: string }) => m.paymentProvider === 'Stripe' || m.paymentProvider === 'Swish' || m.paymentProvider === 'NetsConnectCloud',
          ),
        )
        const dl = data.restaurant.deliveryLocations || []
        setDeliveryLocations(dl)
        if (dl.length > 0) setSelectedDeliveryLoc(dl[0].id)
        setTimeSlots((data.restaurant.deliveryTimeSlots || []).filter((s: { disabled: boolean }) => !s.disabled))
        setShowsTip(data.restaurant.showsTipInClient ?? false)
        setQueueTime(data.restaurant.queueTime || null)

        // Hämta till-specifika pong.*-attribut i en SEPARAT query som kan
        // misslyckas utan att bryta hela kiosken. Tidigare fanns detta inlined
        // i MENU_QUERY men LocoPOS kräver auth för att läsa tills.attribute(),
        // vilket sprängde kiosken för nya enheter utan login.
        try {
          const attrData = await gql(config.apiEndpoint, KIOSK_ATTRS_QUERY, { id: config.restaurantId }, parseToken || undefined)
          const tills = attrData?.restaurant?.tills || []
          if (tills.length > 0) {
            const sorted = [...tills].sort((a: { id: string }, b: { id: string }) => a.id.localeCompare(b.id))
            const first = sorted[0]
            try {
              const rawAddons = first?.addonLibrary
              if (rawAddons) {
                const parsed = JSON.parse(rawAddons)
                if (Array.isArray(parsed?.sequences)) {
                  useKioskStore.getState().setAvailableAddons(parsed.sequences)
                }
              }
            } catch {
              /* ignore */
            }
            try {
              const rawMeta = first?.altGroupMeta
              if (rawMeta) {
                const parsedMeta = JSON.parse(rawMeta)
                if (parsedMeta && typeof parsedMeta === 'object' && !Array.isArray(parsedMeta)) {
                  useKioskStore.getState().setAltGroupMeta(parsedMeta)
                }
              }
            } catch {
              /* ignore */
            }
            const rawMaster = first?.altGroupV2Enabled
            useKioskStore.getState().setAltGroupV2MasterEnabled(rawMaster !== 'false')
          }
        } catch (e) {
          // Till-attributes kräver auth — anonyma kiosker får defaults.
          console.log('[kiosk] Till-attributes ej åtkomliga (förmodligen anonym):', e instanceof Error ? e.message : e)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : t('error.menuLoad', locale))
        setStep('error')
      } finally {
        setLoading(false)
      }
    },
    [config, parseToken, locale],
  )

  const isLandscape = remoteConfig?.layoutMode === 'landscape'
  // showsMenuType is the native Till field: 'All' | 'EatIn' | 'TakeAway'.
  // 'All' → render the choice screen; otherwise auto-advance to the only allowed type.
  const showsMenuType = useKioskStore((s) => s.showsMenuType)
  const menuTypes: MenuType[] = showsMenuType === 'All' ? ['EatIn', 'TakeAway'] : [showsMenuType]
  const singleType = menuTypes.length === 1 ? menuTypes[0] : null

  // Auto-advance when only one menu type is configured
  useEffect(() => {
    if (singleType) handleSelect(singleType)
  }, [singleType, handleSelect])

  /* ───── Bakgrundsbildspel ─────
   * Slides kommer från remoteConfig.startScreenMedia[] (bilder/videos
   * från backoffice). Auto-detection av video vs image baserat på
   * filändelse. */
  const slides = useMemo(() => resolveSlideshow(remoteConfig?.startScreenMedia), [remoteConfig?.startScreenMedia])
  const _firstSlideUrl = slides[0]?.url
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0)

  // Reset till slide 0 när media-listan byts
  useEffect(() => {
    setCurrentSlideIdx(0)
  }, [])

  // Schemalägg nästa slide för IMAGE-slides — videor triggas via onEnded
  useEffect(() => {
    if (slides.length <= 1) return
    const slide = slides[currentSlideIdx]
    if (!slide || slide.type === 'video') return
    const ms = (slide.durationSec || 5) * 1000
    const id = window.setTimeout(() => {
      setCurrentSlideIdx((i) => (i + 1) % slides.length)
    }, ms)
    return () => window.clearTimeout(id)
  }, [slides, currentSlideIdx])

  const advanceSlide = useCallback(() => {
    if (slides.length <= 1) return
    setCurrentSlideIdx((i) => (i + 1) % slides.length)
  }, [slides.length])

  const showDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1'

  // Skip rendering the UI while auto-advancing so the buttons don't flash
  if (singleType) return null

  const eatInSvg = (size: string) => (
    <svg viewBox="0 0 24 24" className={`${size} fill-black shrink-0`}>
      <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" />
    </svg>
  )
  const takeAwaySvg = (size: string) => (
    <svg viewBox="0 0 24 24" className={`${size} fill-black shrink-0`}>
      <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" />
    </svg>
  )

  return (
    <div className="h-full w-full flex flex-col bg-black relative overflow-hidden">
      {/* Bakgrundsmedia (bildspel / singel video / bild) */}
      <div className="absolute inset-0">
        {slides.length > 0 &&
          slides.map((slide, idx) => (
            <div key={`${slide.url}-${idx}`} className="absolute inset-0 transition-opacity duration-500" style={{ opacity: idx === currentSlideIdx ? 1 : 0 }}>
              {slide.type === 'video' ? (
                <video src={slide.url} autoPlay muted playsInline preload="auto" loop={slides.length === 1} onEnded={advanceSlide} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <img src={slide.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
            </div>
          ))}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Bildspel-indikator (dots) — bara om mer än en slide */}
      {slides.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5">
          {slides.map((_, idx) => (
            <span
              key={idx}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                backgroundColor: idx === currentSlideIdx ? 'white' : 'rgba(255,255,255,0.3)',
                width: idx === currentSlideIdx ? '20px' : '6px',
              }}
            />
          ))}
        </div>
      )}

      {/* Debug-overlay — aktiveras med ?debug=1 */}
      {showDebug && (
        <div className="fixed top-2 left-2 right-2 z-50 bg-black/95 border border-yellow-400 rounded-lg p-3 text-xs font-mono text-white max-h-[60vh] overflow-y-auto">
          <div className="font-bold text-yellow-400 mb-2">🔍 KIOSK DEBUG (?debug=1)</div>
          <div className="space-y-1">
            <div>
              <span className="text-zinc-400">Slides totalt:</span> {slides.length}
            </div>
            <hr className="border-zinc-700 my-2" />
            <div className="font-bold text-zinc-300">RemoteConfig (Till.attribute):</div>
            <div className="break-all">
              <span className="text-zinc-400">startScreenMedia:</span> {JSON.stringify(remoteConfig?.startScreenMedia ?? null)}
            </div>
            <hr className="border-zinc-700 my-2" />
            <div className="font-bold text-zinc-300">Slides:</div>
            {slides.length === 0 ? (
              <div className="text-red-400">⚠️ INGA slides</div>
            ) : (
              slides.map((s, i) => (
                <div key={i} className={`break-all p-1 rounded ${i === currentSlideIdx ? 'bg-yellow-900/40' : ''}`}>
                  [{i}] <span className="text-purple-400">{s.type}</span> <span className="text-zinc-500">{s.durationSec ? `(${s.durationSec}s)` : ''}</span>{' '}
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-400 underline">
                    {s.url || '(tom)'}
                  </a>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {isLandscape ? (
        /* ─── Landscape layout ─── Logo top, buttons side-by-side, language bottom */
        <>
          <div className="relative z-10 shrink-0 flex flex-col items-center pt-10 pb-6">
            {remoteConfig?.logoUrl && (
              <div className="w-36 h-36 rounded-full overflow-hidden border-3 border-white/20 shadow-2xl bg-black/40">
                <img src={remoteConfig.logoUrl} alt={remoteConfig?.appName ?? ''} className="w-full h-full object-cover" />
              </div>
            )}
            <h1 className="text-3xl font-bold text-kiosk-accent mt-5 tracking-widest drop-shadow-lg">{(remoteConfig?.appName || 'Restaurang').toUpperCase()}</h1>
          </div>

          <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-12">
            <div className={`grid gap-6 w-full ${menuTypes.length === 2 ? 'max-w-3xl grid-cols-2' : 'max-w-md grid-cols-1'}`}>
              {menuTypes.includes('EatIn') && (
                <button
                  onClick={() => handleSelect('EatIn')}
                  disabled={loading}
                  className="kiosk-btn flex items-center justify-center gap-5 py-10 px-10 bg-white/95 rounded-3xl shadow-2xl shadow-black/30 disabled:opacity-50"
                >
                  {eatInSvg('w-12 h-12')}
                  <span className="text-3xl font-bold text-black">{t('menuType.eatIn', locale)}</span>
                </button>
              )}
              {menuTypes.includes('TakeAway') && (
                <button
                  onClick={() => handleSelect('TakeAway')}
                  disabled={loading}
                  className="kiosk-btn flex items-center justify-center gap-5 py-10 px-10 bg-white/95 rounded-3xl shadow-2xl shadow-black/30 disabled:opacity-50"
                >
                  {takeAwaySvg('w-12 h-12')}
                  <span className="text-3xl font-bold text-black">{t('menuType.takeAway', locale)}</span>
                </button>
              )}
            </div>
          </div>

          <div className="relative z-20 pb-10 shrink-0">
            <LanguageToggle />
          </div>
        </>
      ) : (
        /* ─── Portrait layout (original) ─── */
        <>
          <div className="relative z-10 shrink-0 flex flex-col items-center pt-12 pb-4">
            {remoteConfig?.logoUrl && (
              <div className="w-28 h-28 rounded-full overflow-hidden border-3 border-white/20 shadow-2xl bg-black/40">
                <img src={remoteConfig.logoUrl} alt={remoteConfig?.appName ?? ''} className="w-full h-full object-cover" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-kiosk-accent mt-4 tracking-widest drop-shadow-lg">{(remoteConfig?.appName || 'Restaurang').toUpperCase()}</h1>
          </div>

          <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8">
            <div className="flex flex-col gap-5 w-full max-w-sm">
              {menuTypes.includes('EatIn') && (
                <button
                  onClick={() => handleSelect('EatIn')}
                  disabled={loading}
                  className="kiosk-btn flex items-center justify-center gap-4 py-7 px-8 bg-white/95 rounded-2xl shadow-lg shadow-black/20 disabled:opacity-50"
                >
                  {eatInSvg('w-9 h-9')}
                  <span className="text-2xl font-bold text-black">{t('menuType.eatIn', locale)}</span>
                </button>
              )}
              {menuTypes.includes('TakeAway') && (
                <button
                  onClick={() => handleSelect('TakeAway')}
                  disabled={loading}
                  className="kiosk-btn flex items-center justify-center gap-4 py-7 px-8 bg-white/95 rounded-2xl shadow-lg shadow-black/20 disabled:opacity-50"
                >
                  {takeAwaySvg('w-9 h-9')}
                  <span className="text-2xl font-bold text-black">{t('menuType.takeAway', locale)}</span>
                </button>
              )}
            </div>
          </div>

          <div className="relative z-20 pb-8 shrink-0">
            <LanguageToggle />
          </div>
        </>
      )}
    </div>
  )
}
